import camelCase from "lodash/camelCase";
import { CamelCase } from "type-fest";
import { columns } from "./fields/Columns";
import { filters } from "./fields/Filters";
import { applyPermissions } from "./fields/Permission";
import { paymentOptions } from "./fields/PaymentOptions";
import type { FinancialProduct, PricePoint } from "./PricePoints";
import { PricingFieldsPayload } from "./getPricingFields.types";
import {
  CASH,
  FLEX_MONTHLY,
  LOAN,
  MONTHLY,
  PREPAID,
} from "constants/financialProducts";

const financialProductsOrderedList: FinancialProduct[] = [
  CASH,
  LOAN,
  MONTHLY,
  PREPAID,
  FLEX_MONTHLY,
];

const financialProductsCamelCaseOrderedList: CamelCase<FinancialProduct>[] =
  financialProductsOrderedList.map((fp) => {
    return camelCase(fp) as CamelCase<FinancialProduct>;
  });

const PRICING_UNAVAILABLE_MESSAGE =
  "Pricing could not be provided for this offer. Please change the included products or contact support";

const defaultFinancialProductPriorityList: FinancialProduct[] = [
  FLEX_MONTHLY,
  MONTHLY,
  PREPAID,
  CASH,
  LOAN,
];

const getDefaultFinancialProduct = (
  availableProducts: FinancialProduct[]
): FinancialProduct | undefined =>
  defaultFinancialProductPriorityList.find((product) =>
    availableProducts.includes(product)
  );

// For context, in order to catch a mismatch in financial product and unified pricing data
// https://sunrun.jira.com/browse/SPLAT1-5673
const verifyFinancialProductAvailability = (
  payload: PricingFieldsPayload
): boolean =>
  Boolean(
    payload?.financialProduct &&
      payload.unifiedPricingData[
        camelCase(payload.financialProduct) as CamelCase<FinancialProduct>
      ]
  );

const getPricingFields = (payload: PricingFieldsPayload) => {
  if (!verifyFinancialProductAvailability(payload)) {
    return null;
  }
  const filteredColumns = columns
    .filter((c) => applyPermissions(payload, c.permission))
    .map((c) => ({
      ...c,
      value: (pricePoint: PricePoint) => c.value(pricePoint, payload),
      displayFilter: (value: unknown, pricePoint?: PricePoint) =>
        c.displayFilter(value, pricePoint, payload),
    }));
  const filteredPaymentOptions = paymentOptions.filter((po) =>
    applyPermissions(payload, po.permission)
  );
  const filteredFilters = filters.filter((f) =>
    applyPermissions(payload, f.permission)
  );

  return {
    columns: filteredColumns,
    paymentOptions: filteredPaymentOptions,
    filters: filteredFilters,
  };
};

export {
  getPricingFields,
  getDefaultFinancialProduct,
  financialProductsOrderedList,
  defaultFinancialProductPriorityList,
  financialProductsCamelCaseOrderedList,
  PRICING_UNAVAILABLE_MESSAGE,
};
export type { PricingFieldsPayload };
